import { EMPTY_ARRAY, isMultileg, LedgerAccountTypeEnum, type Security } from '@talos/kyoko';
import { MultilegComboType } from '../../../components/MultilegCombo/enums';
import type { PanelConfig, PanelType } from '../../../types/LayoutConfig';

export function getDefaultPanelConfig(
  panelType: PanelType,
  securities: Security[]
): { defaultParams: Partial<PanelConfig>; defaultTitle?: string } {
  const nonMultilegSecurities = securities.filter(security => !isMultileg(security));
  switch (panelType) {
    case 'marketCards': {
      return {
        defaultParams: {},
        defaultTitle: 'Market',
      };
    }
    case 'predictionMarkets':
      return {
        defaultParams: {
          columns: [],
          sortCards: false,
        },
        defaultTitle: 'Prediction Markets',
      };
    case 'priceLadder':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} prices`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'volumeLadder':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} volume`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'tradesHistory':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} trades`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'deepDive':
      return {
        defaultTitle: nonMultilegSecurities[0]?.DisplaySymbol,
        defaultParams: {
          type: 'deepDive',
          symbol: nonMultilegSecurities[0]?.Symbol,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          chartResolution: '1H',
          annotations: [],
          chartType: 'candlestick',
          showMarkets: false,
          closable: true,
          editable: true,
        },
      };
    case 'orderBook':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} Book`,
        defaultParams: {
          type: 'orderBook',
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          showCumQty: true,
          closable: true,
          editable: true,
        },
      };
    case 'historicalPriceChart':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} Chart`,
        defaultParams: {
          type: 'historicalPriceChart',
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          chartResolution: '1H',
          annotations: [],
          chartType: 'candlestick',
          closable: true,
          editable: true,
        },
      };
    case 'multilegDeepDive':
      return {
        defaultParams: {
          type: 'multilegDeepDive',
          resolvedSymbol: undefined,
          instrumentType: MultilegComboType.SyntheticCross,
          initiatingLegs: [],
          compactOrderBook: false,
          legSymbols: [],
          legMarketAccounts: [],
          legMarkets: [],
          closable: true,
          editable: true,
        },
      };
    case 'options':
      return {
        defaultParams: {
          type: 'options',
          closable: true,
          editable: true,
        },
      };
    case 'reconMismatchesDetails':
      return {
        defaultParams: {
          type: 'reconMismatchesDetails',
          mismatchIDs: [],
          closable: true,
          editable: true,
        },
      };
    case 'accountLedgerEvents':
      return {
        defaultParams: {
          type: 'accountLedgerEvents',
          asset: undefined,
          account: '',
          accountType: LedgerAccountTypeEnum.MarketAccount,
          closable: true,
          editable: true,
        },
      };
    case 'positionAutoHedgingRules':
      return {
        defaultParams: {
          type: 'positionAutoHedgingRules',
          closable: true,
          editable: true,
        },
      };
    case 'dealProductBuilder':
      return {
        defaultParams: {
          type: 'dealProductBuilder',
          bookingType: 'forward',
          closable: true,
          editable: true,
        },
      };
    case 'aggregatedAssetMetric':
      return {
        defaultParams: {
          type: 'aggregatedAssetMetric',
          // No `settingsInitialOpen` — the panel ships with a default metric, asset basket and
          // aggregation, so it opens straight into the chart. Assets, metric and Sum/Mean are all
          // editable from the header toolbar, and the settings cog reaches the full config view.
        },
      };
    case 'fundingRateBlotter':
      return {
        defaultParams: {
          type: 'fundingRateBlotter',
          // Opens the config view on first mount so the user configures assets /
          // update mode / default view before the blotter fetches. Cleared on setup
          // completion (Done), not on mount, so a reload mid-setup resumes config.
          settingsInitialOpen: true,
        },
      };
    default:
      return { defaultParams: {} };
  }
}
